'use client';

import { createContext, ReactNode, useContext, useEffect } from "react";
import { HistoryEvent, HistoryEventInput, PublicationStatus } from "@/app/lib/generated/graphql-types";
import {
    useCollectionTypeEditor,
    UseCollectionTypeEditorType,
} from "@/app/lib/hooks/use-editor/useCollectionTypeEditor";
import { HistoryEventEditable } from "@/app/features/sites/history/types/types";
import { HistoryEventSchema } from "@/app/features/sites/history/types/schema";
import { getHistoryEvent } from "@/app/features/sites/history/data-access/queries";
import { updateHistoryEvent } from "@/app/features/sites/history/data-access/mutations";
import { useSiteHistoryEditor } from "@/app/features/sites/history/contexts/SiteHistoryEditor";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";

const HistoryEventEditorContext = createContext<UseCollectionTypeEditorType<HistoryEventEditable> | undefined>(undefined);

export function useHistoryEventEditor() {
    const context = useContext(HistoryEventEditorContext);
    if (!context) {
        throw new Error('useHistoryEventEditor must be used within HistoryEventEditorProvider');
    }
    return context;
}

function editableFromHistoryEvent(event: HistoryEvent): HistoryEventEditable {
    return {
        title: event.title,
        description: event.description,
        startDate: event.startDate,
        endDate: event.endDate,
        showDateAs: event.showDateAs,
        image: event.image,
        links: event.links?.filter((link) => link != null),
        readMore: event.readMore,
    };
}

export default function HistoryEventEditorProvider({
    children,
    documentId,
}: {
    children: ReactNode;
    documentId: string;
}) {
    const { registerChildEditor, unregisterChildEditor } = useSiteHistoryEditor();
    const editor = useCollectionTypeEditor<HistoryEvent, HistoryEventEditable, HistoryEventInput>({
        documentId,
        schema: HistoryEventSchema,
        loadEntity: getHistoryEvent,
        update: updateHistoryEvent,
        toEditable: editableFromHistoryEvent,
        autosave: async (targetDocumentId, data) => updateHistoryEvent({
            documentId: targetDocumentId,
            data,
            status: PublicationStatus.Draft,
        }),
        manageDraftMode: false,
    });

    useEffect(() => {
        registerChildEditor(documentId, {
            hasChanges: editor.hasChanges,
            autosaveState: editor.autosaveState,
        }, {
            publish: editor.publish,
            discardChanges: editor.discardChanges,
        });

        return () => unregisterChildEditor(documentId);
    }, [
        documentId,
        editor.autosaveState,
        editor.discardChanges,
        editor.hasChanges,
        editor.publish,
        registerChildEditor,
        unregisterChildEditor,
    ]);

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <HistoryEventEditorContext.Provider value={editor}>
            {children}
        </HistoryEventEditorContext.Provider>
    );
}
